import React from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { usePolling, getPollingQueryOpts } from "../layout/HoaAdminLayout"
import { useLanguage } from "@/contexts/LanguageContext"
import {
  getFinancialSummary,
  requestReport,
  type PeriodKey,
  type FinancialSummaryResponse,
} from "@/api/reports"
import { toast } from "sonner"
import { listBuildings } from "@/api/buildings"
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts"

/** =========================================================
 * Period options
 * ========================================================= */
const PERIODS: PeriodKey[] = [
  "current_month",
  "last_month",
  "3months",
  "6months",
  "year",
]

/** =========================================================
 * Page
 * ========================================================= */
export default function ReportsPage() {
  const { enabled, intervalMs } = usePolling()
  const { t, language } = useLanguage()
  const locale = language === "es" ? "es-VE" : "en-US"
  const [period, setPeriod] = React.useState<PeriodKey>("current_month")
  const [lastReportEmail, setLastReportEmail] = React.useState<string | null>(null)
  const [buildingId, setBuildingId] = React.useState<string>("")

  const condosQuery = useQuery({
    queryKey: ["hoa-admin", "buildings"],
    queryFn: listBuildings,
    ...getPollingQueryOpts(false, 0),
  })
  const condos = condosQuery.data || []

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["hoa-admin", "reports", period, buildingId],
    queryFn: () => getFinancialSummary(period, buildingId || undefined),
    ...getPollingQueryOpts(enabled, intervalMs),
  })

  const reportMutation = useMutation({
    mutationFn: () => requestReport(period, buildingId || undefined),
    onSuccess: (res) => {
      setLastReportEmail(res.email)
      toast.success(t("reports.toast.submitted.title"), {
        description: t("reports.toast.submitted.desc", { email: res.email }),
        duration: 6000,
      })
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.detail ||
        t("reports.toast.error.generic")
      if (err?.response?.status === 429) {
        toast.error(t("common.warning"), {
          description: t("reports.toast.error.limit"),
          duration: 6000,
        })
      } else {
        toast.error(msg)
      }
    },
  })

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-[18px] font-semibold text-[color:var(--color-fg)]">{t("reports.title")}</h1>
          <p className="text-sm text-[--color-muted]">{t("reports.subtitle")}</p>
        </div>
        <div className="flex-shrink-0 w-full sm:w-64">
          <select
              className="w-full text-base font-medium text-gray-800 border-0 bg-white ring-1 shadow-sm ring-black/10 rounded-2xl px-4 py-3 cursor-pointer hover:ring-[#1F3A93]/50 focus:ring-2 focus:ring-[#1F3A93] transition-all outline-none appearance-none"
              style={{
                backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25em 1.25em',
                paddingRight: '2.5rem'
              }}
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
          >
            <option value="">{t("common.allCondos")}</option>
            {condos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => refetch()}
          className="rounded-xl border border-[--color-border] bg-white px-3 py-2 text-sm"
        >
          {isFetching ? t("common.refreshing") : t("common.refresh")}
        </button>
      </div>

      {/* Period tabs */}
      <div className="flex items-center gap-2 overflow-x-auto">
        {PERIODS.map((p) => (
          <Tab key={p} active={period === p} onClick={() => setPeriod(p)}>
            {t(`reports.period.${p}`)}
          </Tab>
        ))}
      </div>

      {/* Request full report via email */}
      <div className="flex flex-col gap-2">
        <button
          onClick={() => reportMutation.mutate()}
          disabled={reportMutation.isPending}
          className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: "linear-gradient(90deg,#1F3A93 0%,#1F3A93 70%,#27AE60 100%)" }}
        >
          <MailIcon className="h-5 w-5" />
          {reportMutation.isPending ? t("reports.button.requesting") : t("reports.button.request")}
        </button>
        <p className="text-xs text-[--color-muted]">
          {t("reports.notice.csv")}
        </p>
      </div>

      {/* Email sent confirmation */}
      {lastReportEmail && (
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <span className="mt-0.5 text-emerald-600">✅</span>
          <div className="flex-1">
            <div className="text-sm font-medium text-emerald-800">{t("reports.alert.queued")}</div>
            <div className="text-xs text-emerald-700">{t("reports.alert.sent")} <strong>{lastReportEmail}</strong> {t("reports.alert.sentSuffix")}</div>
          </div>
          <button onClick={() => setLastReportEmail(null)} className="text-emerald-400 hover:text-emerald-600 text-lg leading-none">&times;</button>
        </div>
      )}

      {isLoading || !data ? (
        <div className="space-y-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : (
        <>
          {/* Financial Summary */}
          <Card>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="grid size-8 place-items-center rounded-xl bg-[#E6F7EE] text-[#27AE60]">
                  <DollarIcon className="h-5 w-5" />
                </div>
                <h2 className="text-[16px] font-semibold">{t("reports.financial.title")}</h2>
              </div>
              <button
                className="text-sm text-[--color-primary] hover:underline"
                onClick={() => reportMutation.mutate()}
                disabled={reportMutation.isPending}
              >
                {reportMutation.isPending ? t("reports.button.requesting") : t("reports.button.emailReport")}
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-6">
              <Stat label={t("reports.financial.totalRevenue")} value={currency(data.total_revenue, locale)} />
              <Stat label={t("reports.financial.collected")} value={currency(data.collected, locale)} color="#27AE60" />
              <Stat label={t("reports.financial.pending")} value={currency(data.pending, locale)} color="#F59E0B" />
              <Stat label={t("reports.financial.overdue")} value={currency(data.overdue, locale)} color="#EF4444" />
            </div>

            <div className="mt-2 flex items-center gap-6">
              <div>
                <div className="text-sm text-[--color-muted]">{t("reports.financial.collectionRate")}</div>
                <div className="text-[18px] font-semibold text-[color:var(--color-fg)]">
                  {Math.round(data.collection_rate * 100)}%
                </div>
              </div>
              <div>
                <div className="text-sm text-[--color-muted]">{t("reports.financial.invoiceCount")}</div>
                <div className="text-[18px] font-semibold text-[color:var(--color-fg)]">
                  {data.invoice_count}
                </div>
              </div>
            </div>

            {/* Collection status bar chart */}
            {(data.collected > 0 || data.pending > 0 || data.overdue > 0) && (
              <div className="mt-5">
                <div className="mb-2 text-[15px] font-semibold">{t("reports.financial.statusChart")}</div>
                <div className="text-xs text-[--color-muted] mb-3">{t("reports.financial.statusChartDesc")}</div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* Bar chart */}
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={[
                        { name: t("reports.financial.collected"), value: data.collected, fill: "#27AE60" },
                        { name: t("reports.financial.pending"), value: data.pending, fill: "#F59E0B" },
                        { name: t("reports.financial.overdue"), value: data.overdue, fill: "#EF4444" },
                      ]}
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "#6B7280" }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#6B7280" }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v: number) => compactCurrency(v, locale)}
                      />
                      <Tooltip
                        formatter={(value: number) => [currency(value, locale), t("common.amount")]}
                        contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 13 }}
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                      />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={60}>
                        {["#27AE60", "#F59E0B", "#EF4444"].map((color, idx) => (
                          <Cell key={idx} fill={color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Collection rate radial */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-sm text-[--color-muted] mb-1">{t("reports.financial.collectionRate")}</div>
                    <ResponsiveContainer width={160} height={160}>
                      <RadialBarChart
                        cx="50%"
                        cy="50%"
                        innerRadius="60%"
                        outerRadius="90%"
                        startAngle={90}
                        endAngle={-270}
                        data={[{ value: Math.round(data.collection_rate * 100), fill: "#27AE60" }]}
                      >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar
                          dataKey="value"
                          cornerRadius={8}
                          background={{ fill: "#E5E7EB" }}
                        />
                        <text
                          x="50%"
                          y="50%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="fill-[color:var(--color-fg)]"
                          style={{ fontSize: 22, fontWeight: 700 }}
                        >
                          {Math.round(data.collection_rate * 100)}%
                        </text>
                      </RadialBarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Trend chart */}
            {data.trend_data.length > 0 ? (
              <div className="mt-5">
                <div className="mb-2 text-[15px] font-semibold">{t("reports.financial.trendChart")}</div>
                <div className="text-xs text-[--color-muted] mb-3">{t("reports.financial.trendChartDesc")}</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.trend_data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={{ stroke: "#E5E7EB" }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#6B7280" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v: number) => compactCurrency(v, locale)}
                    />
                    <Tooltip
                      formatter={(value: number) => [currency(value, locale), undefined]}
                      contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 13 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 13 }} />
                    <Line
                      type="monotone"
                      dataKey="collected"
                      name={t("reports.financial.collected")}
                      stroke="#27AE60"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: "#27AE60" }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="pending"
                      name={t("reports.financial.pending")}
                      stroke="#F59E0B"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: "#F59E0B" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="mt-5 rounded-xl border border-dashed border-[--color-border] bg-[#F5F6FA] p-6 text-center text-sm text-[--color-muted]">
                <div className="mx-auto mb-2 grid size-10 place-items-center rounded-full bg-white">
                  <BarsIcon className="h-5 w-5 text-[--color-muted]" />
                </div>
                {t("reports.financial.noData")}
              </div>
            )}
          </Card>

          {/* Action cards */}
          <ActionCard
            icon={<ReportIcon className="h-6 w-6 text-[#1F3A93]" />}
            title={t("reports.action.monthly.title")}
            desc={t("reports.action.monthly.desc")}
            cta={reportMutation.isPending ? t("reports.button.requesting") : t("reports.button.emailReport")}
            onClick={() => reportMutation.mutate()}
            disabled={reportMutation.isPending}
          />
          <ActionCard
            icon={<BarsIcon className="h-6 w-6 text-[#27AE60]" />}
            title={t("reports.action.resident.title")}
            desc={t("reports.action.resident.desc")}
            cta={reportMutation.isPending ? t("reports.button.requesting") : t("reports.button.emailReport")}
            onClick={() => reportMutation.mutate()}
            disabled={reportMutation.isPending}
          />
        </>
      )}
    </div>
  )
}

/** =========================================================
 * UI bits
 * ========================================================= */
function Tab({ active, onClick, children }: { active?: boolean; onClick?: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="rounded-full px-4 py-2 text-sm font-medium"
      style={{
        color: active ? "#fff" : "var(--color-fg)",
        background: active ? "#1F3A93" : "#EEF2FF",
        boxShadow: active ? "inset 0 -2px 0 rgba(0,0,0,0.12)" : "none",
      }}
    >
      {children}
    </button>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[--color-border] bg-white p-5 shadow-sm">
      {children}
    </div>
  )
}

/* 🔧 Added skeleton so loading state renders without errors */
function CardSkeleton() {
  return (
    <div className="animate-pulse rounded-2xl border border-[--color-border] bg-white p-5">
      <div className="h-4 w-1/3 rounded bg-gray-200" />
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="h-6 rounded bg-gray-200" />
        <div className="h-6 rounded bg-gray-200" />
        <div className="h-6 rounded bg-gray-200" />
      </div>
      <div className="mt-4 h-24 rounded bg-gray-100" />
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div>
      <div className="text-[20px] font-semibold" style={{ color: color ?? "var(--color-fg)" }}>
        {value}
      </div>
      <div className="text-sm text-[--color-muted]">{label}</div>
    </div>
  )
}

function ActionCard({
  icon, title, desc, cta, onClick, disabled,
}: {
  icon: React.ReactNode
  title: string
  desc: string
  cta: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-[--color-border] bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="grid size-10 place-items-center rounded-2xl bg-[#F5F6FA]">{icon}</div>
        <div className="flex-1">
          <div className="text-[16px] font-semibold">{title}</div>
          <div className="text-sm text-[--color-muted]">{desc}</div>
        </div>
        <button
          onClick={onClick}
          disabled={disabled}
          className="rounded-xl border border-[--color-border] bg-white px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
        >
          {cta}
        </button>
      </div>
    </div>
  )
}

/** =========================================================
 * Helpers
 * ========================================================= */
function currency(n: number, locale: string) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n)
  } catch {
    return `$${n.toLocaleString()}`
  }
}

function compactCurrency(n: number, locale: string) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n)
  } catch {
    return `$${n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n}`
  }
}

/** =========================================================
 * Inline icons (no deps)
 * ========================================================= */
function MailIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M2 7l10 6 10-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  )
}
function DollarIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M12 1v22M8 7c0-1.657 1.79-3 4-3s4 1.343 4 3-1.79 3-4 3-4 1.343-4 3 1.79 3 4 3 4-1.343 4-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  )
}
function BarsIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M4 20h4V8H4v12zm6 0h4V4h-4v16zm6 0h4v-6h-4v6z" fill="currentColor" />
    </svg>
  )
}
function BuildingIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M8 7h2M8 11h2M8 15h2M14 7h2M14 11h2M14 15h2M3 13h18" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  )
}
function AlertIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L14.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}
function ReportIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M6 2h9l3 3v17H6z" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  )
}
function UsersIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2 19a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="17" cy="9" r="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M17 14a5 5 0 0 1 5 5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}
function CalendarIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M8 2v4M16 2v4M3 9h18" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  )
}
